import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext.jsx'
import { CompanyProvider } from './context/CompanyContext.jsx'
import { FeedbackProvider } from './components/ui/feedback.jsx'
import AppLayout from './layout/AppLayout.jsx'
import LoginPage from './features/auth/LoginPage.jsx'
import ForgotPasswordPage from './features/auth/ForgotPasswordPage.jsx'
import ResetPasswordPage from './features/auth/ResetPasswordPage.jsx'
import RegisterWizard from './features/auth/RegisterWizard.jsx'
import LandingPage from './features/public/LandingPage.jsx'
import LegalPage from './features/public/LegalPage.jsx'
import HelpCenterPage from './features/help/HelpCenterPage.jsx'
import DashboardPage from './features/dashboard/DashboardPage.jsx'
import InvoicesListPage from './features/invoices/InvoicesListPage.jsx'
import InvoiceFormPage from './features/invoices/InvoiceFormPage.jsx'
import InvoiceDetailPage from './features/invoices/InvoiceDetailPage.jsx'
import PartyListPage from './features/parties/PartyListPage.jsx'
import PartyDetailPage from './features/parties/PartyDetailPage.jsx'
import { PARTY_CONFIG } from './features/parties/partyConfig.js'
import ArticlesListPage from './features/articles/ArticlesListPage.jsx'
import SettingsPage from './features/settings/SettingsPage.jsx'
import AdminPage from './features/admin/AdminPage.jsx'
import OnboardingList from './features/admin/OnboardingList.jsx'
import OnboardingDetails from './features/admin/OnboardingDetails.jsx'
import AdminScreen from './features/admin/AdminScreen.jsx'
import CompaniesTab from './features/admin/CompaniesTab.jsx'
import AdminUsersTab from './features/admin/AdminUsersTab.jsx'
import AppSettingsTab from './features/admin/AppSettingsTab.jsx'
import DebugPage from './features/admin/DebugPage.jsx'
import CompanyPendingValidationPage from './features/auth/CompanyPendingValidationPage.jsx'
import SetupPage from './features/onboarding/SetupPage.jsx'
import { TOKEN_KEY } from './lib/api.js'
import Placeholder from './components/ui/Placeholder.jsx'

/*
 * Route map for frontend-ng. Public auth routes + the protected /app/* shell.
 * Screens land as <Placeholder> until each re-implementation phase replaces
 * them with the real feature (see docs/inventory + docs/REIMPLEMENTATION.md).
 */
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
          <FeedbackProvider>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterWizard />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/legal/:type" element={<LegalPage />} />

              <Route path="/app" element={<AppLayout />}>
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="invoices" element={<InvoicesListPage />} />
                <Route path="invoices/new" element={<InvoiceFormPage />} />
                <Route path="invoices/:id/edit" element={<InvoiceFormPage />} />
                <Route path="invoices/:id" element={<InvoiceDetailPage />} />
                <Route path="articles" element={<ArticlesListPage />} />
                <Route path="customers" element={<PartyListPage config={PARTY_CONFIG.customers} />} />
                <Route path="customers/:id" element={<PartyDetailPage config={PARTY_CONFIG.customers} />} />
                <Route path="suppliers" element={<PartyListPage config={PARTY_CONFIG.suppliers} />} />
                <Route path="suppliers/:id" element={<PartyDetailPage config={PARTY_CONFIG.suppliers} />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="admin" element={<AdminPage />} />
                <Route path="admin/companies" element={<AdminScreen titleKey="admin.tabs.companies"><CompaniesTab /></AdminScreen>} />
                <Route path="admin/users" element={<AdminScreen titleKey="admin.tabs.users"><AdminUsersTab /></AdminScreen>} />
                <Route path="admin/app-settings" element={<AdminScreen titleKey="admin.tabs.appSettings"><AppSettingsTab /></AdminScreen>} />
                <Route path="admin/debug" element={<DebugPage />} />
                <Route path="admin/onboarding" element={<OnboardingList />} />
                <Route path="admin/onboarding/:id" element={<OnboardingDetails />} />
                <Route path="help" element={<HelpCenterPage />} />
                <Route path="help/:articleId" element={<HelpCenterPage />} />
              </Route>

              {/* Full-screen, token-guarded (no sidebar). Reachable right after an
                  incomplete-onboarding login, before a full user profile loads. */}
              <Route path="/app/company-pending-validation" element={<TokenGate><CompanyPendingValidationPage /></TokenGate>} />
              <Route path="/app/setup" element={<TokenGate><SetupPage /></TokenGate>} />

              {/* Public help — shareable article URLs (no account needed). */}
              <Route path="/help" element={<HelpCenterPage variant="public" />} />
              <Route path="/help/:articleId" element={<HelpCenterPage variant="public" />} />

              <Route path="/landing" element={<LandingPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </FeedbackProvider>
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

/* Token-only guard for full-screen flows reachable before a full profile loads. */
function TokenGate({ children }) {
  return localStorage.getItem(TOKEN_KEY) ? children : <Navigate to="/login" replace />
}

/* Root: public landing for anonymous visitors, dashboard for signed-in users. */
function RootRoute() {
  const { isAuthenticated, loading } = useAuth()
  if (loading) return null
  return isAuthenticated ? <Navigate to="/app/dashboard" replace /> : <LandingPage />
}
